/**
 * React Navigation linking configuration - web.
 *
 * On web, React Navigation drives the browser's address bar and Back/Forward
 * buttons through this config. Two things are configured here that the native
 * build has no use for:
 *
 *   1. the public URL map (webRoutes.ts), so the address bar reads /products
 *      rather than /ProductStack/ProductList - 15.15, 15.17, 15.18; and
 *
 *   2. a filter on both directions of the path <-> state conversion that keeps
 *      the permission flags out of the URL entirely - 15.16, 15.24.
 *
 * webpack-dev-server is configured with historyApiFallback, so a deep link
 * pasted straight into the browser resolves to index.html and then to the
 * matching screen.
 *
 * Metro resolves linking.ts instead, so Android's deep-link behaviour is
 * exactly what it was.
 */

import {
  getPathFromState as defaultGetPathFromState,
  getStateFromPath as defaultGetStateFromPath,
  type LinkingOptions,
} from '@react-navigation/native';

import {
  ORDERS_TAB_PATH,
  PERMISSION_PARAMS,
  REDUNDANT_URL_PARAMS,
  TAB_ONLY_URL_PARAMS,
  webScreenPaths,
} from './webRoutes';

const origin =
  typeof window !== 'undefined' && window.location ? window.location.origin : '';

/**
 * Drops the named params from a path's query string.
 *
 * Callers pass different lists in each direction. Outbound drops both
 * PERMISSION_PARAMS and REDUNDANT_URL_PARAMS, so neither reaches the address
 * bar; inbound drops only PERMISSION_PARAMS, so a hand-edited URL cannot
 * smuggle a permission flag into navigation state.
 *
 * Every other query parameter is left exactly as it is - `isReadOnly` on the
 * image gallery, `id` on an edit screen and so on are real navigation state
 * and have to survive a refresh.
 */
function stripQueryParams(path: string, names: string[]): string {
  const queryAt = path.indexOf('?');
  if (queryAt === -1) {
    return path;
  }

  const base = path.slice(0, queryAt);
  const kept = path
    .slice(queryAt + 1)
    .split('&')
    .filter(pair => {
      const name = decodeURIComponent(pair.split('=')[0]);
      return !names.includes(name);
    });

  return kept.length > 0 ? `${base}?${kept.join('&')}` : base;
}

type NavState = {
  routes?: Array<{
    name?: string;
    params?: Record<string, unknown>;
    state?: NavState;
  }>;
};

/**
 * Belt and braces for the inbound direction: the query filter above already
 * removes the flags, but a path config could in principle bind one as a path
 * segment. Removing them from the parsed state as well means there is exactly
 * one way for a screen to learn its permissions, and it is not the URL.
 *
 * Nothing is lost by doing this. No screen reads these from its route - all 42
 * permission-aware screens call `getScreenPermission(stack)` (redux/hooks.ts),
 * which reads `common.drawerMenuList`, the permission set restored from the
 * API at sign-in. The params were only ever passed alongside, and React
 * Navigation serialised them into the URL because they were there.
 */
function stripPermissionParams<T extends NavState | undefined>(state: T): T {
  if (!state || !state.routes) {
    return state;
  }

  for (const route of state.routes) {
    if (route.params) {
      for (const name of PERMISSION_PARAMS) {
        delete route.params[name];
      }
      if (Object.keys(route.params).length === 0) {
        delete route.params;
      }
    }
    if (route.state) {
      stripPermissionParams(route.state);
    }
  }

  return state;
}

export const linking: LinkingOptions<ReactNavigation.RootParamList> = {
  enabled: true,
  prefixes: [origin, 'zenbasketPos://'].filter(Boolean),

  /*
   * The root config carries both the AuthStack screens and the DrawerStack
   * screens. Only one of those navigators is mounted at a time (App.tsx picks
   * by isLoggedIn), and React Navigation matches the config against whichever
   * tree is actually mounted, so the unused half is simply ignored.
   */
  config: {
    screens: webScreenPaths as LinkingOptions<ReactNavigation.RootParamList>['config'] extends
      | {screens: infer S}
      | undefined
      ? S
      : never,
  },

  getPathFromState(state, options) {
    const path = defaultGetPathFromState(state, options);
    return stripQueryParams(path, [
      ...PERMISSION_PARAMS,
      ...REDUNDANT_URL_PARAMS,
      ...(ORDERS_TAB_PATH.test(path) ? TAB_ONLY_URL_PARAMS : []),
    ]);
  },

  getStateFromPath(path, options) {
    return stripPermissionParams(
      defaultGetStateFromPath(
        stripQueryParams(path, PERMISSION_PARAMS),
        options,
      ) as NavState,
    ) as ReturnType<typeof defaultGetStateFromPath>;
  },
};

/**
 * Browser tab titles.
 *
 * React Navigation's web default is the route *name*, so tabs read "SignIn",
 * "LoginPin", "NewSellScreen". Screens that set `options.title` keep it;
 * the rest get a readable label derived from the name.
 */
const SCREEN_TITLES: Record<string, string> = {
  SignIn: 'Sign in',
  Register: 'Register',
  ForgotPin: 'Forgot PIN',
  LoginPin: 'Enter PIN',
  ChangePin: 'Change PIN',
  UpdatePin: 'Set PIN',
  SignUp: 'Sign up',
  dashboard: 'Dashboard',
  NewSellScreen: 'Sell',
  OrderSummary: 'Checkout',
};

const APP_TITLE = 'ZenBasket POS';

function humanize(name: string): string {
  const words = name
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export const documentTitle = {
  formatter: (
    options: Record<string, any> | undefined,
    route: {name: string} | undefined,
  ): string => {
    const label =
      (typeof options?.title === 'string' && options.title) ||
      (route?.name && (SCREEN_TITLES[route.name] ?? humanize(route.name)));
    return label ? `${label} · ${APP_TITLE}` : APP_TITLE;
  },
};

export default linking;
